/**
 * Workforce Intelligence Context
 * 
 * Provides global state management for user's workforce intelligence profile
 * Connected to backend API for persistent storage
 */

import { createContext, useContext, useState, useEffect, useCallback, useMemo } from 'react';
import axios from 'axios';
import { useAuth } from '@/contexts/AuthContext';
import {
  calculateWII,
  classifyWII,
  identifyStrengths,
  identifyGaps,
  determineReadinessLevel,
  generateDevelopmentPathway,
  scoreOpportunityForUser,
  scoreToolForUser,
  DECISION_ENGINE,
  SEVEN_FORCES_ARRAY,
  DEFAULT_USER_PROFILE
} from '@/data/workforceIntelligence';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

// Create context
const WorkforceIntelligenceContext = createContext(null);

/**
 * Workforce Intelligence Provider
 * Wraps the application to provide WII state and functions
 */
export function WorkforceIntelligenceProvider({ children }) {
  const { user, isAuthenticated, token } = useAuth();
  const [profile, setProfile] = useState(DEFAULT_USER_PROFILE);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  // Create axios instance with auth headers
  const authAxios = useMemo(() => {
    const instance = axios.create({
      baseURL: API,
      headers: {
        'Content-Type': 'application/json'
      }
    });
    
    if (token) {
      instance.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
    
    return instance;
  }, [token]);

  // Fetch profile from backend when authenticated
  useEffect(() => {
    const fetchProfile = async () => {
      if (!isAuthenticated || !token) {
        setIsLoading(false);
        return;
      }

      setIsLoading(true);
      setError(null);

      try {
        const response = await authAxios.get('/user/wii-profile');
        const backendProfile = response.data;
        
        // Transform backend response to frontend profile format
        const transformedProfile = {
          id: backendProfile.user_id,
          createdAt: backendProfile.created_at,
          updatedAt: backendProfile.updated_at,
          assessmentCompleted: backendProfile.assessment_completed,
          assessmentDate: backendProfile.assessment_date,
          forceScores: backendProfile.force_scores,
          wiiScore: backendProfile.wii_score,
          wiiLevel: backendProfile.wii_level,
          readinessLevel: backendProfile.readiness_level,
          strengths: backendProfile.strengths.map(s => s.id),
          gaps: backendProfile.gaps.map(g => g.id),
          certificationLevel: backendProfile.certification_level,
          preferences: profile.preferences, // Keep local preferences
          recommendations: profile.recommendations // Keep local recommendations
        };
        
        setProfile(transformedProfile);
      } catch (err) {
        if (err.response?.status === 404) {
          // No profile yet, that's okay
          setProfile({
            ...DEFAULT_USER_PROFILE,
            id: user?.user_id || null
          });
        } else {
          console.error('Error fetching WII profile:', err);
          setError(err.message);
        }
      } finally {
        setIsLoading(false);
      }
    };

    fetchProfile();
  }, [isAuthenticated, token, user?.user_id, authAxios]);

  /**
   * Save force scores to backend
   */
  const saveForceScores = useCallback(async (forceScores, assessmentResponses = null, metadata = null) => {
    if (!isAuthenticated || !token) {
      throw new Error('Authentication required to save profile');
    }

    setIsSaving(true);
    setError(null);

    try {
      const payload = {
        force_scores: forceScores,
        assessment_responses: assessmentResponses,
        metadata: metadata
      };

      const response = await authAxios.post('/user/wii-profile', payload);
      const backendProfile = response.data;
      
      // Transform and update local state
      const transformedProfile = {
        id: backendProfile.user_id,
        createdAt: backendProfile.created_at,
        updatedAt: backendProfile.updated_at,
        assessmentCompleted: backendProfile.assessment_completed,
        assessmentDate: backendProfile.assessment_date,
        forceScores: backendProfile.force_scores,
        wiiScore: backendProfile.wii_score,
        wiiLevel: backendProfile.wii_level,
        readinessLevel: backendProfile.readiness_level,
        strengths: backendProfile.strengths.map(s => s.id),
        gaps: backendProfile.gaps.map(g => g.id),
        certificationLevel: backendProfile.certification_level,
        preferences: profile.preferences,
        recommendations: profile.recommendations
      };
      
      setProfile(transformedProfile);
      return backendProfile;
    } catch (err) {
      console.error('Error saving WII profile:', err);
      setError(err.message);
      throw err;
    } finally {
      setIsSaving(false);
    }
  }, [isAuthenticated, token, authAxios, profile.preferences, profile.recommendations]);

  /**
   * Submit assessment results and transform to WII profile
   */
  const submitAssessment = useCallback(async (assessmentResponses, metadata = null) => {
    if (!isAuthenticated) {
      throw new Error('Please log in to save your assessment results');
    }

    // Let the backend transform assessment responses to force scores
    return saveForceScores(null, assessmentResponses, metadata);
  }, [isAuthenticated, saveForceScores]);

  /**
   * Update force scores locally (for demo/testing) and sync to backend
   */
  const updateForceScores = useCallback(async (forceScores) => {
    // Update local state first for immediate UI feedback
    const newProfile = {
      ...profile,
      forceScores,
      wiiScore: calculateWII(forceScores),
      wiiLevel: classifyWII(calculateWII(forceScores))?.id,
      readinessLevel: determineReadinessLevel(forceScores),
      strengths: identifyStrengths(forceScores).map(s => s.id),
      gaps: identifyGaps(forceScores).map(g => g.id),
      assessmentCompleted: true,
      updatedAt: new Date().toISOString()
    };
    setProfile(newProfile);

    // Sync to backend if authenticated
    if (isAuthenticated && token) {
      try {
        await saveForceScores(forceScores);
      } catch (err) {
        console.error('Failed to sync to backend:', err);
        // Keep local state, will sync later
      }
    }
  }, [profile, isAuthenticated, token, saveForceScores]);

  /**
   * Update a single force score
   */
  const updateSingleForce = useCallback((forceId, score) => {
    const newForceScores = {
      ...profile.forceScores,
      [forceId]: Math.min(100, Math.max(0, score))
    };
    updateForceScores(newForceScores);
  }, [profile.forceScores, updateForceScores]);

  /**
   * Update user preferences (local only)
   */
  const updatePreferences = useCallback((preferences) => {
    setProfile(prev => ({
      ...prev,
      preferences: {
        ...prev.preferences,
        ...preferences
      },
      updatedAt: new Date().toISOString()
    }));
  }, []);

  /**
   * Generate certificate from backend
   */
  const generateCertificate = useCallback(async (certificateType = 'wii_assessment') => {
    if (!isAuthenticated || !token) {
      throw new Error('Authentication required to generate certificate');
    }

    try {
      const response = await authAxios.post('/user/certificates', {
        certificate_type: certificateType
      });
      return response.data;
    } catch (err) {
      console.error('Error generating certificate:', err);
      throw err;
    }
  }, [isAuthenticated, token, authAxios]);

  /**
   * Get user's certificates from backend
   */
  const getCertificates = useCallback(async () => {
    if (!isAuthenticated || !token) {
      return [];
    }

    try {
      const response = await authAxios.get('/user/certificates');
      return response.data;
    } catch (err) {
      console.error('Error fetching certificates:', err);
      return [];
    }
  }, [isAuthenticated, token, authAxios]);

  /**
   * Reset profile
   */
  const resetProfile = useCallback(() => {
    setProfile({
      ...DEFAULT_USER_PROFILE,
      id: user?.user_id || null
    });
  }, [user?.user_id]);

  /**
   * Score an opportunity for this user
   */
  const scoreOpportunity = useCallback((opportunity) => {
    return scoreOpportunityForUser(opportunity, profile);
  }, [profile]);

  /**
   * Score a tool for this user
   */
  const scoreTool = useCallback((tool) => {
    return scoreToolForUser(tool, profile);
  }, [profile]);

  /**
   * Get personalized development pathway
   */
  const getDevelopmentPathway = useCallback(() => {
    return generateDevelopmentPathway(profile);
  }, [profile]);

  /**
   * Get opportunity filter based on profile
   */
  const getOpportunityFilter = useCallback(() => {
    return DECISION_ENGINE.getOpportunityFilter(profile.wiiScore);
  }, [profile.wiiScore]);

  /**
   * Get tool filter based on profile
   */
  const getToolFilter = useCallback(() => {
    return DECISION_ENGINE.getToolComplexityFilter(profile.readinessLevel);
  }, [profile.readinessLevel]);

  /**
   * Get gap-based development priorities
   */
  const getDevelopmentPriorities = useCallback(() => {
    return DECISION_ENGINE.mapGapsToDevelopment(profile.gaps);
  }, [profile.gaps]);

  /**
   * Get strength-based opportunity recommendations
   */
  const getStrengthOpportunities = useCallback(() => {
    return DECISION_ENGINE.mapStrengthsToOpportunities(profile.strengths);
  }, [profile.strengths]);

  // Memoized computed values
  const wiiLevel = useMemo(() => classifyWII(profile.wiiScore), [profile.wiiScore]);
  
  const strengthDetails = useMemo(() => {
    if (!profile.forceScores) return [];
    return identifyStrengths(profile.forceScores);
  }, [profile.forceScores]);
  
  const gapDetails = useMemo(() => {
    if (!profile.forceScores) return [];
    return identifyGaps(profile.forceScores);
  }, [profile.forceScores]);

  const hasCompletedAssessment = useMemo(() => {
    return profile.assessmentCompleted && profile.wiiScore !== null;
  }, [profile.assessmentCompleted, profile.wiiScore]);

  // Context value
  const value = useMemo(() => ({
    // State
    profile,
    isLoading,
    isSaving,
    error,
    hasCompletedAssessment,
    isAuthenticated,
    
    // Computed values
    wiiScore: profile.wiiScore,
    wiiLevel,
    readinessLevel: profile.readinessLevel,
    forceScores: profile.forceScores,
    strengths: profile.strengths,
    gaps: profile.gaps,
    strengthDetails,
    gapDetails,
    certificationLevel: profile.certificationLevel,
    
    // Actions
    updateForceScores,
    updateSingleForce,
    updatePreferences,
    submitAssessment,
    saveForceScores,
    resetProfile,
    generateCertificate,
    getCertificates,
    
    // Personalization functions
    scoreOpportunity,
    scoreTool,
    getDevelopmentPathway,
    getOpportunityFilter,
    getToolFilter,
    getDevelopmentPriorities,
    getStrengthOpportunities
  }), [
    profile,
    isLoading,
    isSaving,
    error,
    hasCompletedAssessment,
    isAuthenticated,
    wiiLevel,
    strengthDetails,
    gapDetails,
    updateForceScores,
    updateSingleForce,
    updatePreferences,
    submitAssessment,
    saveForceScores,
    resetProfile,
    generateCertificate,
    getCertificates,
    scoreOpportunity,
    scoreTool,
    getDevelopmentPathway,
    getOpportunityFilter,
    getToolFilter,
    getDevelopmentPriorities,
    getStrengthOpportunities
  ]);

  return (
    <WorkforceIntelligenceContext.Provider value={value}>
      {children}
    </WorkforceIntelligenceContext.Provider>
  );
}

/**
 * Hook to access workforce intelligence context
 */
export function useWorkforceIntelligence() {
  const context = useContext(WorkforceIntelligenceContext);
  if (!context) {
    throw new Error('useWorkforceIntelligence must be used within a WorkforceIntelligenceProvider');
  }
  return context;
}

export default WorkforceIntelligenceContext;
